import { ACTIONS, EXT_QUOTE_ACTIONS, FULL_QUOTE_STEPS } from "../AppConstants";
import { IDriverBase, ICreditCardInfo, IContactInfo, IAdditionalDriver } from "../classes/Domain";
import { IExternalQuote, IVehicle, IAgreement, IBindQuoteResult, IESignatureForm, IViolation } from "../classes/ExternalQuote";
import BravoApiClient from "../classes/BravoApiClient";
import { validationUtils } from "../validation/Validation";
import { allFieldsValidation as carValidation } from "../validation/fullQuote/CarValidation";
import { allFieldsValidation as driverValidation } from "../validation/fullQuote/DriverValidation";
import { allFieldsValidation as creditCardPaymentValidation } from "../validation/fullQuote/CreditCardPaymentValidation";
import { allFieldsValidation as esigValidation } from "../validation/fullQuote/ESignatureValidation";
import { allFieldsValidation as contactInfoValidation } from "../validation/fullQuote/ContactInfoValidation";
import { allViolationsValidation as violationsValidation } from "../validation/fullQuote/ViolationValidation";

const apiClient = new BravoApiClient();

export const loadQuote = (quote: any) => (dispatch) => {

    dispatch({
        type: ACTIONS.LOAD_QUOTE,
        quote: quote
    });

    return Promise.resolve();
};

export const fillExternalQuote = (quote: IExternalQuote) => (dispatch) => {
    dispatch({
        type: ACTIONS.FILL_EXTERNAL_QUOTE,
        quote: quote
    });

    return Promise.resolve();
};

export const fillRtrQuote = (quote: IExternalQuote, paymentChoice: any) => (dispatch) => {
    dispatch({
        type: ACTIONS.FILL_RTR_QUOTE,
        quote: quote,
        paymentChoice: paymentChoice
    });

    return Promise.resolve();
};

export const updateVehicle = (
    quoteId: number,
    vehicle: IVehicle,
    paymentPlanCode : number,
    updateLienholder: boolean = false,
    updateGaragingAddress: boolean = false) => (dispatch) => {

    const { isValid, errors } = carValidation(vehicle);

    if (!isValid) {
        validationUtils.publishValidationFailure(errors);        
        return Promise.reject();
    }

    var promise = apiClient.updateVehicle(quoteId, paymentPlanCode, vehicle, updateLienholder, updateGaragingAddress);
        
    promise.then(() => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.UPDATE_VEHICLE,
            vehicle: vehicle
        });
    });

    return promise;
};

export const reloadVehicle = (quoteId: number, vehicleId: number, vehicleChanged: boolean, quoteRerated: boolean) => (dispatch) => {
    var promise = apiClient.getVehicle(quoteId, vehicleId);

    promise.then((response) => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.UPDATE_VEHICLE,
            vehicle: response.data,
            vehicleChanged,
            quoteRerated
        });
    });

    return promise;
};

export const updateAgreement = (quoteId: number, agreement: IAgreement, contactInfo: IContactInfo) => (dispatch) =>
{
    const { isValid, errors } = contactInfoValidation(contactInfo);

    if (!isValid) {
        validationUtils.publishValidationFailure(errors);
        return Promise.reject();
    }

    var promise = apiClient.updateContactInfo(quoteId, contactInfo);

    promise.then((response) => {

        dispatch({
            type: EXT_QUOTE_ACTIONS.UPDATE_AGREEMENT,
            agreement: agreement,
            contactInfo: contactInfo
        });
    });

    return promise;
};

export const resetAgreement = () => (dispatch) => {
    dispatch({
        type: EXT_QUOTE_ACTIONS.RESET_AGREEMENT
    });
};

export const saveCreditCardPaymentInfo = (quoteId: number, creditCardPaymentInfo: ICreditCardInfo) => (dispatch) => {
    // dispatch == store.dispatch

    const { isValid, errors } = creditCardPaymentValidation(creditCardPaymentInfo);
    if (!isValid) {
        validationUtils.publishValidationFailure(errors);
        return Promise.reject();
    }
        
    var promise = apiClient.saveCreditCardInfo(quoteId, creditCardPaymentInfo);

    promise.then((response) => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.UPDATE_PAYMENT_PLAN,
            paymentPlanCode: response.data.paymentPlanCode
        });
    });

    return promise;
};

export const updateDriver = (quoteId: number, paymentPlanCode: number, driver: IDriverBase) => (dispatch) => {
    const { isValid, errors } = driverValidation(driver);
    if (!isValid) {
        validationUtils.publishValidationFailure(errors);
        return Promise.reject();
    }

    var promise = apiClient.updateDriver(quoteId, paymentPlanCode, driver);

    promise.then(() => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.UPDATE_DRIVER,
            driver: driver,
        });
    });

    return promise;
};

export const getViolations = (quoteId: number) => (dispatch) => {
    var promise = apiClient.getViolations(quoteId);

    promise.then((response) => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.UPDATE_VIOLATIONS,
            mvrResult: response.data,
            hasAdditionalDrivers: response.data?.additionalDrivers?.length > 0
        });
    });

    return promise;
};

export const getDocuments = (rtrQuoteId: number) => (dispatch) => {
    var promise = apiClient.getDocuments(rtrQuoteId);

    promise.then((response) => {

        dispatch({
            type: EXT_QUOTE_ACTIONS.UPDATE_DOCUMENTS,
            documents: response.data
        });
    });

    return promise;
};

export interface IBindQuoteResponseData {
    bindQuoteResult: IBindQuoteResult,
    esignatureForms: Array<IESignatureForm>
} 

export interface IBindQuoteResponse {
    data: IBindQuoteResponseData
} 

export const bindQuote = (quoteId: number) => (dispatch): Promise<IBindQuoteResponse> => {
    var promise = apiClient.bindQuote(quoteId);

    promise.then((response) => {

        dispatch({
            type: EXT_QUOTE_ACTIONS.BIND_QUOTE,
            bindResult: response.data.bindQuoteResult,
            esignatureForms: response.data.esignatureForms
        });
    });

    return promise;
};

export const reRateQuote = (quoteId: number, paymentPlanCode: number, hasMvrViolations: boolean = false, hasAdditionalDrivers: boolean = false) => (dispatch) => {
    var promise = apiClient.reRateQuote(quoteId, paymentPlanCode);

    promise.then((response) => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.RERATE_QUOTE,
            data: { ...response.data, hasMvrViolations, hasAdditionalDrivers }
        });
    });

    return promise;
};

export const reSetQuoteEffDate = (quoteId: number, paymentPlanCode: number, effectiveDate: string) => (dispatch) => {
    var promise = apiClient.reSetQuoteEffDate(quoteId, paymentPlanCode, effectiveDate);

    promise.then((response) => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.RESET_QUOTE_EFF_DATE,
            data: { ...response.data, effectiveDate }
        });
    });

    return promise;
};


export const reloadQuotePaymentInfo = (quoteId: number, quoteRerated: boolean = true) => (dispatch) => {
    var promise = apiClient.getPaymentInfo(quoteId);

    promise.then((response) => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.RELOAD_QUOTE_PMT_INFO,
            data: { ...response.data, quoteRerated }
        });
    });

    return promise;
};

export const disputeViolations = (quoteId: number, driverId: number, violationsForDispute: Array<IViolation>) => () => {
    const { isValid, errors } = violationsValidation(violationsForDispute);

    if (!isValid) {
        validationUtils.publishValidationFailure(errors);
        return Promise.reject();
    }

    return apiClient.disputeViolations(quoteId, driverId, violationsForDispute);
};

export const getESignatureForms = (rtrQuoteId: number) => (dispatch) => {

    var promise = apiClient.getESignatureForms(rtrQuoteId);

    promise.then((response) => {
        console.log(response.data);
        dispatch({
            type: EXT_QUOTE_ACTIONS.FILL_E_SIGNATURES,
            forms: response.data
        });
    });

    return promise;
};

export const updateESignatureAgreement = (rtrQuoteId: number, esignatureCode: string, insuredName: string, needToSign: boolean, lastForm: boolean, options: any[]) => (dispatch) => {
    if (needToSign) {
        const data = { insuredName, options };
        const { isValid, errors } = esigValidation(data);

        if (!isValid) {
            validationUtils.publishValidationFailure(errors);
            return Promise.reject();
        }
    }

    const promise = apiClient.updateESignatureAgreement(rtrQuoteId, esignatureCode, insuredName, needToSign, lastForm, options);
    return promise;
};

export const updateAdditionalDrivers = (quoteId: number, additionalDrivers: Array<IAdditionalDriver>) => (dispatch) => {
    var hasValidationErrors = false;

    additionalDrivers.filter(x => x.status === "ADD").map((data, i) => {
        let { isValid, errors } = driverValidation(data.driver);
        if (!isValid) {
            var uid = FULL_QUOTE_STEPS.ADDITIONAL_DRIVERS + "_" + data.driver.id;
            validationUtils.publishValidationFailureByUid(errors, uid);
            hasValidationErrors = true;
        }
    });

    if (hasValidationErrors) {
        return Promise.reject();
    }

    var promise = apiClient.updateAdditionalDrivers(quoteId, additionalDrivers);

    promise.then((response) => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.UPDATE_ADDITIONAL_DRIVERS,
            drivers: response.data.drivers,
            additionalDrivers: additionalDrivers
        });
    });

    return promise;
};